// When was it last done, and is it overdue?
//
// Pure functions over the plan's two entities, so the list screen and the
// detail screen answer this question the same way and neither has to reach for
// the other's markup. No Vue in here on purpose: it is testable with plain
// values and it renders nothing.
//
// Every string here is user-facing, and the app's whole voice is finishing the
// sentence "Last time I…" — so the answers read as answers to that, not as
// dates. "14 months ago" is what somebody remembers; "2025-06-14" is what a
// database remembers.

const DAY = 24 * 60 * 60 * 1000;

/** Completions for one task, newest first. */
export function historyFor (completions, taskId) {
  return completions
    .filter((c) => String(c.task) === String(taskId))
    .slice()
    .sort((a, b) => new Date(b.doneAt) - new Date(a.doneAt));
}

/** The most recent doing, or null. */
export function lastDone (completions, taskId) {
  return historyFor(completions, taskId)[0] ?? null;
}

/** Whole days between two dates, positive when `then` is in the past. */
export function daysSince (then, now = new Date()) {
  if (!then) return null;
  const t = new Date(then);
  if (Number.isNaN(t.getTime())) return null;
  // Midnight-to-midnight, so "yesterday" does not become "today" at 11pm.
  const a = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((b - a) / DAY);
}

/**
 * A gap in the words people actually use.
 *
 * Rounded deliberately. Somebody who cleaned the gutters last autumn thinks
 * "about a year ago", not "391 days ago", and a precise number invites a
 * precision the underlying date does not have.
 */
export function humanGap (days) {
  if (days === null) return 'never';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  if (days < 365) return `${Math.round(days / 30)} months ago`;
  const years = days / 365;
  return years < 1.5 ? 'about a year ago' : `${Math.round(years)} years ago`;
}

/** The same gap, forwards. */
function humanUntil (days) {
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 14) return `in ${days} days`;
  if (days < 60) return `in ${Math.round(days / 7)} weeks`;
  if (days < 365) return `in ${Math.round(days / 30)} months`;
  return 'in about a year';
}

/**
 * Everything the list needs about one task, in one pass.
 *
 * `tone` maps to an Ionic colour, and `status` ALWAYS carries the same meaning
 * in words. Colour alone is not a status: it is invisible to a screen reader,
 * it disappears in greyscale, and roughly one man in twelve cannot separate the
 * red from the green.
 */
export function taskStatus (task, completions, now = new Date()) {
  const last = lastDone(completions, task.id);
  const since = daysSince(last?.doneAt, now);
  const interval = Number(task.intervalMonths) || 0;

  const base = {
    lastLabel: last ? humanGap(since) : 'never',
    daysSince: since,
    last,
  };

  // No interval is not a problem to solve. Some things are worth a history and
  // no schedule — the plan says so — and inventing a due date for them would
  // nag somebody about a task they deliberately left open.
  if (!interval) {
    return { ...base, tone: 'medium', status: last ? 'No schedule' : 'Never done', sortKey: since === null ? 6000 : 5000 - since };
  }

  if (!last) {
    return { ...base, tone: 'primary', status: 'Never done', sortKey: 1000 };
  }

  // Months as calendar months, not 30-day blocks: "every 6 months" from the
  // 14th means the 14th, which is what a person putting it in their head does.
  const due = new Date(last.doneAt);
  due.setMonth(due.getMonth() + interval);
  const untilDays = -daysSince(due, now);

  if (untilDays < 0) {
    return { ...base, tone: 'danger', status: `Overdue by ${humanGap(-untilDays).replace(/ ago$/, '')}`, sortKey: untilDays };
  }
  if (untilDays <= 14) {
    return { ...base, tone: 'warning', status: `Due ${humanUntil(untilDays)}`, sortKey: 2000 + untilDays };
  }
  return { ...base, tone: 'success', status: `Due ${humanUntil(untilDays)}`, sortKey: 3000 + untilDays };
}

/**
 * The list, most-overdue first.
 *
 * The plan's primary screen is "every task sorted by how overdue it is", and
 * that ordering is the whole product: the thing you have not done for longest
 * is the thing you opened the app to find.
 */
export function sortedTasks (tasks, completions, now = new Date()) {
  return tasks
    .filter((t) => !t.archived)
    .map((task) => ({ task, ...taskStatus(task, completions, now) }))
    .sort((a, b) => a.sortKey - b.sortKey || String(a.task.name).localeCompare(String(b.task.name)));
}
